import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Image as ImageIcon,
  FileVideo,
  Sparkles,
  Eye,
  MessageSquare,
} from 'lucide-react';

export default function ContentApprovalPage() {
  const { user, isAdmin, isOperator } = useAuth();
  const [pendingContent, setPendingContent] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('approve');
  const [comment, setComment] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        api.get('/content/pending'),
        api.get('/content'),
      ]);
      setPendingContent(pendingRes.data);
      setAllContent(allRes.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    try {
      await api.post(`/content/${selectedContent.id}/approve`, {
        action: actionType,
        comment: comment || null,
      });
      toast.success(`Content ${actionType === 'approve' ? 'approved' : 'rejected'}`);
      setActionDialogOpen(false);
      setComment('');
      setSelectedContent(null);
      loadContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const openActionDialog = (content, action) => {
    setSelectedContent(content);
    setActionType(action);
    setComment('');
    setActionDialogOpen(true);
  };

  const openPreview = (content) => {
    setPreviewContent(content);
    setPreviewOpen(true);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="w-5 h-5 text-blue-500" />;
      case 'video':
        return <FileVideo className="w-5 h-5 text-purple-500" />;
      case 'widget':
        return <Sparkles className="w-5 h-5 text-amber-500" />;
      default:
        return <ImageIcon className="w-5 h-5 text-zinc-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      case 'pending':
      default:
        return (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin && !isOperator) {
    return (
      <div className="glass-card rounded-xl p-12 text-center">
        <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <h3 className="text-xl font-semibold text-white mb-2">Access Restricted</h3>
        <p className="text-zinc-400">Only operators and administrators can approve content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-approval-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
          Content Approval
        </h1>
        <p className="text-zinc-400 mt-1">Review and approve content submissions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  {pendingContent.length}
                </p>
                <p className="text-sm text-zinc-400">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  {allContent.filter(c => c.approval_status === 'approved').length}
                </p>
                <p className="text-sm text-zinc-400">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-rose-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  {allContent.filter(c => c.approval_status === 'rejected').length}
                </p>
                <p className="text-sm text-zinc-400">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approval Section */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Clock className="w-5 h-5 text-amber-500" />
            Pending Review ({pendingContent.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingContent.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
              <p className="text-white font-medium">All caught up!</p>
              <p className="text-zinc-500 text-sm">No content pending review</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingContent.map((content) => (
                <div
                  key={content.id}
                  className="rounded-lg border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-colors"
                >
                  <div className="aspect-video bg-zinc-800 relative">
                    {content.thumbnail_url || content.url ? (
                      <img
                        src={content.thumbnail_url || content.url}
                        alt={content.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {getTypeIcon(content.type)}
                      </div>
                    )}
                    <Badge className="absolute top-2 left-2 bg-black/50 backdrop-blur-sm border-0">
                      {content.type}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm hover:bg-black/70"
                      onClick={() => openPreview(content)}
                    >
                      <Eye className="w-4 h-4 text-white" />
                    </Button>
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-medium truncate">{content.name}</h3>
                    <p className="text-sm text-zinc-500 mt-1">
                      {content.duration}s • {content.resolution}
                    </p>
                    <div className="flex items-center gap-2 mt-4">
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                        onClick={() => openActionDialog(content, 'approve')}
                        data-testid={`approve-${content.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
                        onClick={() => openActionDialog(content, 'reject')}
                        data-testid={`reject-${content.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            All Content
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {allContent.slice(0, 10).map((content) => (
              <div
                key={content.id}
                className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden">
                    {content.thumbnail_url || content.url ? (
                      <img
                        src={content.thumbnail_url || content.url}
                        alt={content.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getTypeIcon(content.type)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{content.name}</p>
                    <p className="text-sm text-zinc-500">{content.type} • {content.resolution}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {content.approval_comment && (
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <MessageSquare className="w-3 h-3" />
                      <span className="truncate max-w-[100px]">{content.approval_comment}</span>
                    </div>
                  )}
                  {getStatusBadge(content.approval_status)}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {actionType === 'approve' ? 'Approve Content' : 'Reject Content'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedContent && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                <div className="w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden">
                  {selectedContent.thumbnail_url || selectedContent.url ? (
                    <img
                      src={selectedContent.thumbnail_url || selectedContent.url}
                      alt={selectedContent.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getTypeIcon(selectedContent.type)
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedContent.name}</p>
                  <p className="text-sm text-zinc-500">{selectedContent.type} • {selectedContent.resolution}</p>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm text-zinc-400">Comment (optional)</label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder={actionType === 'approve' ? 'Add approval notes...' : 'Reason for rejection...'}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              className={actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-rose-600 hover:bg-rose-500'}
              data-testid="confirm-action-btn"
            >
              {actionType === 'approve' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Content Preview
            </DialogTitle>
          </DialogHeader>
          {previewContent && (
            <div className="space-y-4">
              <div className="aspect-video bg-zinc-800 rounded-lg overflow-hidden">
                {previewContent.type === 'video' ? (
                  <video
                    src={previewContent.url}
                    controls
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <img
                    src={previewContent.url}
                    alt={previewContent.name}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-zinc-500">Name</p>
                  <p className="text-white">{previewContent.name}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Type</p>
                  <p className="text-white capitalize">{previewContent.type}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Duration</p>
                  <p className="text-white">{previewContent.duration} seconds</p>
                </div>
                <div>
                  <p className="text-zinc-500">Resolution</p>
                  <p className="text-white">{previewContent.resolution}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
